import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import axios from "axios";
import { toast } from "sonner";
import {
  Plug, Search, User, Shield, Stethoscope, FileText, Pill,
  ClipboardList, Activity, AlertTriangle, CheckCircle, XCircle,
  Loader2, ArrowRight, ExternalLink, Building2, Heart, Eye,
  Database, Globe, Lock, ChevronRight, Plus, RefreshCw
} from "lucide-react";

const EHR_ICONS = {
  smart: Globe,
  hapi: Database,
  epic: Heart,
  cerner: Building2,
  ecw: ClipboardList,
  athena: Stethoscope,
  nextgen: Activity,
  meditech: Shield
};

export default function EHRConnect() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [smartSession, setSmartSession] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientData, setPatientData] = useState(null);
  const [fetchingPatient, setFetchingPatient] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [creatingDraft, setCreatingDraft] = useState(false);
  const [activeTab, setActiveTab] = useState("connect");

  useEffect(() => {
    fetchVendors();
    // Check for stored SMART session from callback
    const storedSession = sessionStorage.getItem("smart_session");
    if (storedSession) {
      try {
        const session = JSON.parse(storedSession);
        setSmartSession({ session_id: session.session_id, fhir_url: session.fhir_url, ehr_id: session.ehr_id });
        setActiveTab("search");
        sessionStorage.removeItem("smart_session");
        toast.success("Connected to EHR via SMART on FHIR!");
      } catch (e) {
        console.error("Failed to parse stored session", e);
      }
    }
  }, []);

  const fetchVendors = async () => {
    try {
      const res = await axios.get(`${API}/smart/ehr-vendors`);
      setVendors(res.data.vendors || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const connectToEHR = async (ehrId) => {
    setConnecting(true);
    try {
      const callbackUrl = `${window.location.origin}/smart-callback`;
      const res = await axios.post(`${API}/smart/launch/standalone`,
        { ehr_id: ehrId, callback_url: callbackUrl },
        { withCredentials: true }
      );

      if (res.data.type === "open") {
        setSmartSession({ session_id: res.data.session_id, fhir_url: res.data.fhir_url, ehr_id: ehrId });
        setActiveTab("search");
        toast.success(`Connected to ${res.data.fhir_url}`);
      } else if (res.data.type === "smart") {
        window.location.href = res.data.authorization_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Connection failed");
    } finally {
      setConnecting(false);
    }
  };

  const searchPatients = async () => {
    if (!smartSession || !searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await axios.get(`${API}/smart/patients/search`, {
        params: { session_id: smartSession.session_id, name: searchQuery },
        withCredentials: true
      });
      setSearchResults(res.data.patients || []);
      if (res.data.patients?.length === 0) {
        toast.info("No patients found matching your search");
      }
    } catch (err) {
      toast.error("Patient search failed");
    } finally {
      setSearching(false);
    }
  };

  const fetchPatientData = async (patient) => {
    setSelectedPatient(patient);
    setFetchingPatient(true);
    try {
      const res = await axios.get(`${API}/smart/patient/${patient.fhir_id}`, {
        params: { session_id: smartSession.session_id },
        withCredentials: true
      });
      setPatientData(res.data);
      setActiveTab("preview");
      setShowPreview(true);
    } catch (err) {
      toast.error("Failed to fetch patient data");
    } finally {
      setFetchingPatient(false);
    }
  };

  const createPADraft = async () => {
    if (!patientData) return;
    setCreatingDraft(true);
    try {
      const res = await axios.post(`${API}/smart/create-pa-draft`,
        { fhir_data: patientData },
        { withCredentials: true }
      );
      toast.success(`PA Draft created! ID: ${res.data.pa_id}`);
      navigate(`/pa-requests/${res.data.pa_id}/workflow`);
    } catch (err) {
      toast.error("Failed to create PA draft");
    } finally {
      setCreatingDraft(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="ehr-connect-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Plug className="w-6 h-6 text-cyan-400" />
              EHR Connect
            </h1>
            <p className="text-slate-400">Import patient data from EHR systems via SMART on FHIR</p>
          </div>
          {smartSession && (
            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 px-3 py-1.5">
              <CheckCircle className="w-4 h-4 mr-1" />
              Connected to FHIR Server
            </Badge>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-800 border border-slate-700">
            <TabsTrigger value="connect" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
              <Plug className="w-4 h-4 mr-2" />
              Connect
            </TabsTrigger>
            <TabsTrigger value="search" disabled={!smartSession} className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
              <Search className="w-4 h-4 mr-2" />
              Search Patients
            </TabsTrigger>
            <TabsTrigger value="preview" disabled={!patientData} className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
              <Eye className="w-4 h-4 mr-2" />
              Preview & Import
            </TabsTrigger>
          </TabsList>

          {/* Tab 1: Connect to EHR */}
          <TabsContent value="connect" className="mt-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {vendors.map((vendor) => {
                const Icon = EHR_ICONS[vendor.logo] || Globe;
                const isActive = vendor.status === "active";
                return (
                  <Card
                    key={vendor.id}
                    className={`bg-slate-900/50 border-slate-800 transition-all duration-200 ${isActive ? "hover:border-cyan-500/50 hover:shadow-lg hover:shadow-cyan-500/10 cursor-pointer" : "opacity-50"}`}
                    data-testid={`ehr-vendor-${vendor.id}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isActive ? "bg-cyan-500/20" : "bg-slate-800"}`}>
                          <Icon className={`w-6 h-6 ${isActive ? "text-cyan-400" : "text-slate-500"}`} />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-white text-sm">{vendor.name}</h3>
                          {isActive ? (
                            <Badge className="bg-emerald-500/20 text-emerald-400 text-[10px] border-0">Available</Badge>
                          ) : (
                            <Badge className="bg-slate-700 text-slate-400 text-[10px] border-0">Coming Soon</Badge>
                          )}
                        </div>
                      </div>
                      {isActive && (
                        <Button
                          size="sm"
                          className="w-full bg-cyan-600 hover:bg-cyan-500 text-white"
                          onClick={() => connectToEHR(vendor.id)}
                          disabled={connecting}
                          data-testid={`connect-${vendor.id}`}
                        >
                          {connecting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plug className="w-4 h-4 mr-1" />}
                          Connect
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* SMART on FHIR info */}
            <Card className="mt-6 bg-slate-900/30 border-slate-800">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                    <Lock className="w-6 h-6 text-indigo-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">SMART on FHIR R4 Integration</h3>
                    <p className="text-slate-400 text-sm leading-relaxed">
                      Priorath uses the SMART on FHIR standard to securely connect with EHR systems.
                      Patient data is imported using industry-standard FHIR R4 resources including
                      Patient, Coverage, Condition, MedicationRequest, ServiceRequest, and more.
                      All connections are HIPAA-compliant with OAuth 2.0 authentication and audit logging.
                    </p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {["FHIR R4", "OAuth 2.0", "HIPAA Compliant", "SMART App Launch", "HL7 Standard"].map((tag) => (
                        <Badge key={tag} variant="outline" className="border-slate-700 text-slate-400 text-xs">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 2: Search Patients */}
          <TabsContent value="search" className="mt-6">
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Search className="w-5 h-5 text-cyan-400" />
                  Search FHIR Patients
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Search the connected EHR for patients to import into a PA request
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3 mb-6">
                  <Input
                    placeholder="Search by patient name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && searchPatients()}
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="fhir-patient-search"
                  />
                  <Button
                    onClick={searchPatients}
                    className="bg-cyan-600 hover:bg-cyan-500"
                    disabled={searching}
                    data-testid="fhir-search-btn"
                  >
                    {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>

                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm text-slate-400 mb-3">{searchResults.length} patient(s) found</p>
                    {searchResults.map((patient, i) => (
                      <div
                        key={patient.fhir_id || i}
                        className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-cyan-500/50 cursor-pointer transition-all"
                        onClick={() => fetchPatientData(patient)}
                        data-testid={`fhir-patient-${patient.fhir_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                              <User className="w-5 h-5 text-cyan-400" />
                            </div>
                            <div>
                              <p className="font-medium text-white">{patient.full_name || `${patient.first_name} ${patient.last_name}`}</p>
                              <p className="text-sm text-slate-400">
                                DOB: {patient.date_of_birth || "N/A"} | Gender: {patient.gender || "N/A"}
                                {patient.mrn && ` | MRN: ${patient.mrn}`}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {fetchingPatient && selectedPatient?.fhir_id === patient.fhir_id ? (
                              <Loader2 className="w-5 h-5 text-cyan-400 animate-spin" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-slate-500" />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 3: Preview & Import */}
          <TabsContent value="preview" className="mt-6">
            {patientData && (
              <div className="space-y-4">
                {/* Patient Banner */}
                <Card className="bg-cyan-500/10 border-cyan-500/30">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-full bg-cyan-500/20 flex items-center justify-center">
                          <User className="w-7 h-7 text-cyan-400" />
                        </div>
                        <div>
                          <h2 className="text-xl font-bold text-white">
                            {patientData.patient?.full_name || "Unknown Patient"}
                          </h2>
                          <p className="text-sm text-slate-400">
                            DOB: {patientData.patient?.date_of_birth} | Gender: {patientData.patient?.gender}
                            | FHIR ID: {patientData.patient?.fhir_id}
                          </p>
                        </div>
                      </div>
                      <Badge className="bg-cyan-500/20 text-cyan-400">
                        <Database className="w-3 h-3 mr-1" />
                        FHIR Import
                      </Badge>
                    </div>
                  </CardContent>
                </Card>

                {/* Missing Fields Alert */}
                {patientData.missing_fields?.length > 0 && (
                  <Card className="bg-amber-500/10 border-amber-500/30">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5" />
                        <div>
                          <h3 className="font-semibold text-amber-400">Missing Fields</h3>
                          <ul className="mt-1 space-y-1">
                            {patientData.missing_fields.map((field, i) => (
                              <li key={i} className="text-sm text-amber-300/80 flex items-center gap-2">
                                <XCircle className="w-3 h-3" /> {field}
                              </li>
                            ))}
                          </ul>
                          <p className="text-xs text-slate-400 mt-2">These can be completed manually in the PA wizard.</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Data Cards Grid */}
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Coverage */}
                  <Card className="bg-slate-900/50 border-slate-800">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm text-white flex items-center gap-2">
                        <Shield className="w-4 h-4 text-purple-400" /> Insurance / Coverage
                        <Badge className="ml-auto bg-slate-800 text-slate-400 text-[10px]">{patientData.coverage?.length || 0}</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {patientData.coverage?.length > 0 ? patientData.coverage.map((cov, i) => (
                        <div key={i} className="p-3 bg-slate-800/50 rounded-lg mb-2">
                          <p className="font-medium text-white text-sm">{cov.insurance_provider || "Unknown Payer"}</p>
                          <p className="text-xs text-slate-400">Member ID: {cov.member_id || "N/A"}</p>
                          {cov.plan_name && <p className="text-xs text-slate-500">Plan: {cov.plan_name}</p>}
                          <Badge className="mt-1 bg-emerald-500/20 text-emerald-400 text-[10px]">{cov.status || "active"}</Badge>
                        </div>
                      )) : <p className="text-sm text-slate-500">No coverage data found</p>}
                    </CardContent>
                  </Card>

                  {/* Conditions/Diagnoses */}
                  <Card className="bg-slate-900/50 border-slate-800">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm text-white flex items-center gap-2">
                        <FileText className="w-4 h-4 text-red-400" /> Diagnoses (ICD-10)
                        <Badge className="ml-auto bg-slate-800 text-slate-400 text-[10px]">{patientData.conditions?.length || 0}</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {patientData.conditions?.length > 0 ? (
                        <div className="space-y-2">
                          {patientData.conditions.slice(0, 5).map((cond, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm">
                              <Badge className="bg-red-500/20 text-red-400 text-[10px] font-mono">{cond.code || "?"}</Badge>
                              <span className="text-slate-300 truncate">{cond.description}</span>
                            </div>
                          ))}
                          {patientData.conditions.length > 5 && (
                            <p className="text-xs text-slate-500">+{patientData.conditions.length - 5} more</p>
                          )}
                        </div>
                      ) : <p className="text-sm text-slate-500">No conditions found</p>}
                    </CardContent>
                  </Card>

                  {/* Medications */}
                  <Card className="bg-slate-900/50 border-slate-800">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm text-white flex items-center gap-2">
                        <Pill className="w-4 h-4 text-blue-400" /> Medications
                        <Badge className="ml-auto bg-slate-800 text-slate-400 text-[10px]">{patientData.medication_requests?.length || 0}</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {patientData.medication_requests?.length > 0 ? (
                        <div className="space-y-2">
                          {patientData.medication_requests.slice(0, 5).map((med, i) => (
                            <div key={i} className="p-2 bg-slate-800/50 rounded text-sm">
                              <p className="text-white font-medium">{med.medication_name}</p>
                              {med.dosage_text && <p className="text-xs text-slate-400">{med.dosage_text}</p>}
                              <Badge className="mt-1 bg-blue-500/20 text-blue-400 text-[10px]">{med.status}</Badge>
                            </div>
                          ))}
                        </div>
                      ) : <p className="text-sm text-slate-500">No medications found</p>}
                    </CardContent>
                  </Card>

                  {/* Service Requests */}
                  <Card className="bg-slate-900/50 border-slate-800">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm text-white flex items-center gap-2">
                        <ClipboardList className="w-4 h-4 text-amber-400" /> Service Requests
                        <Badge className="ml-auto bg-slate-800 text-slate-400 text-[10px]">{patientData.service_requests?.length || 0}</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {patientData.service_requests?.length > 0 ? (
                        <div className="space-y-2">
                          {patientData.service_requests.map((sr, i) => (
                            <div key={i} className="p-2 bg-slate-800/50 rounded text-sm">
                              <p className="text-white font-medium">{sr.service_name}</p>
                              {sr.cpt_code && <Badge className="bg-amber-500/20 text-amber-400 text-[10px] font-mono">CPT: {sr.cpt_code}</Badge>}
                              <Badge className="ml-1 bg-slate-700 text-slate-300 text-[10px]">{sr.status}</Badge>
                            </div>
                          ))}
                        </div>
                      ) : <p className="text-sm text-slate-500">No service requests found</p>}
                    </CardContent>
                  </Card>

                  {/* Encounters */}
                  <Card className="bg-slate-900/50 border-slate-800">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm text-white flex items-center gap-2">
                        <Activity className="w-4 h-4 text-emerald-400" /> Recent Encounters
                        <Badge className="ml-auto bg-slate-800 text-slate-400 text-[10px]">{patientData.encounters?.length || 0}</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {patientData.encounters?.length > 0 ? (
                        <div className="space-y-2">
                          {patientData.encounters.slice(0, 3).map((enc, i) => (
                            <div key={i} className="p-2 bg-slate-800/50 rounded text-sm">
                              <p className="text-white">{enc.type_text || enc.class_code || "Encounter"}</p>
                              <p className="text-xs text-slate-400">{enc.period_start}</p>
                            </div>
                          ))}
                        </div>
                      ) : <p className="text-sm text-slate-500">No encounters found</p>}
                    </CardContent>
                  </Card>

                  {/* Observations (Labs) */}
                  <Card className="bg-slate-900/50 border-slate-800">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm text-white flex items-center gap-2">
                        <Activity className="w-4 h-4 text-teal-400" /> Lab Results
                        <Badge className="ml-auto bg-slate-800 text-slate-400 text-[10px]">{patientData.observations?.length || 0}</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {patientData.observations?.length > 0 ? (
                        <div className="space-y-2">
                          {patientData.observations.slice(0, 5).map((obs, i) => (
                            <div key={i} className="flex items-center justify-between text-sm">
                              <span className="text-slate-300 truncate">{obs.display || obs.code}</span>
                              <span className="text-white font-mono">{obs.value} {obs.unit}</span>
                            </div>
                          ))}
                        </div>
                      ) : <p className="text-sm text-slate-500">No lab results found</p>}
                    </CardContent>
                  </Card>
                </div>

                {/* FHIR Source Info */}
                <Card className="bg-slate-900/30 border-slate-800">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Database className="w-3 h-3" />
                      Source: {patientData.fhir_source?.server} | Fetched: {new Date(patientData.fhir_source?.fetched_at).toLocaleString()}
                    </div>
                  </CardContent>
                </Card>

                {/* Create PA Draft Button */}
                <div className="flex justify-center gap-4 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => { setActiveTab("search"); setPatientData(null); }}
                    className="border-slate-700 text-slate-300"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Search Another Patient
                  </Button>
                  <Button
                    size="lg"
                    onClick={createPADraft}
                    disabled={creatingDraft}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold px-8"
                    data-testid="create-pa-draft-btn"
                  >
                    {creatingDraft ? (
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    ) : (
                      <Plus className="w-5 h-5 mr-2" />
                    )}
                    Create PA Draft from EHR Data
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
